import { DocCard } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import React, { FC, isValidElement, PropsWithChildren, ReactNode } from 'react';

interface MdxCardProps {
  title?: string;
  icon?: ReactNode;
  href?: string;
}

function resolveIconPath(icon?: ReactNode) {
  if (typeof icon !== 'string') {
    return undefined;
  }

  // Fern card icons are sometimes symbolic names such as "rocket". DocCard
  // expects an image path, so only pass through values Next/Image can load.
  return icon.startsWith('/') || /^https?:\/\//.test(icon) ? icon : undefined;
}

function renderInlineIcon(icon?: ReactNode) {
  return isValidElement(icon) ? icon : null;
}

const MdxCard: FC<PropsWithChildren<MdxCardProps>> = ({
  title,
  href,
  icon,
  children,
}) => {
  const iconPath = resolveIconPath(icon);

  return (
    <DocCard
      href={href}
      iconPath={iconPath}
      iconAlt={title || ''}
      trackingId="mdx-card"
    >
      {!iconPath && renderInlineIcon(icon)}
      {title && <strong>{title}</strong>}
      {children}
    </DocCard>
  );
};

MdxCard.displayName = 'MdxCard';

interface MdxCardGroupProps {
  cols?: number;
}

const MdxCardGroup: FC<PropsWithChildren<MdxCardGroupProps>> = ({
  cols = 2,
  children,
}) => {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '16px',
      }}
    >
      {children}
    </Box>
  );
};

MdxCardGroup.displayName = 'MdxCardGroup';

export { MdxCard, MdxCardGroup };
